import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import NoCarrierIcon from '@/assets/icons/NoCarrierIcon';
import { useGetOrganizations } from '@/apis/agency/organizations/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { useGetStates } from 'apis/country/hook';
import {
  useGetAvailableEndpointNumbers,
  usePurchaseTelephonyEndpoint
} from '@/apis/business/setting/telephonyEndpoints/hook';
import { BusinessTelephonyProvider } from '@/apis/business/types';
import {
  findWhitelistedCountry,
  getPhoneNumberPurchaseAccess,
  getPhoneNumberSurchargeAmount,
  isPhoneNumberChargeEnabled,
  normalizeCountryCode
} from '@/utils/phoneNumberBilling';
import { formatDollars } from '@/utils/helperFunctions';
import {
  formatSearchInput,
  isValidPhoneNumberSearch,
  normalizeBusinessState,
  parsePhoneNumberSearch
} from '@/pages/Business/Agent/Configure/DeployPhone/utils/phoneNumberSearch';

type Props = {
  open: boolean;
  onClose: () => void;
};

const COUNTRY_LABELS: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  IE: 'Ireland',
  GB: 'United Kingdom'
};

function purchaseProviders(providers: BusinessTelephonyProvider[] | undefined) {
  return (providers ?? []).filter(
    (provider) =>
      (provider.code === 'twilio' || provider.code === 'bandwidth') &&
      provider.activations?.includes('phone_number_purchase')
  );
}

function hasAnySelfBuy(providers: BusinessTelephonyProvider[]) {
  return providers.some((provider) => (provider.whitelisted_countries ?? []).some((country) => country.self_buy));
}

function formatPhoneNumberDisplay(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(-10);
  if (national.length !== 10) return phoneNumber;
  return `+1 (${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

function ProviderTab({
  active,
  label,
  onClick,
  disabled
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const theme = useTheme();

  return (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        flex: 1,
        textAlign: 'center',
        py: 1.25,
        px: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        bgcolor: active ? 'primary.light' : 'background.paper',
        color: active ? 'primary.main' : 'text.primary',
        fontWeight: 600,
        fontSize: 14,
        borderRight: `1px solid ${theme.palette.primary.light}`,
        '&:last-of-type': { borderRight: 'none' },
        transition: 'all 0.15s ease'
      }}
    >
      {label}
    </Box>
  );
}

export default function BuyNumberDrawer({ open, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data: organizations } = useGetOrganizations();
  const supportEmail = organizations?.support_email;
  const { data: businessProfile, isFetched: isBusinessProfileFetched } = useGetBusinessProfile({
    include: 'telephony_providers,address'
  });
  const { mutateAsync: purchaseNumber, isPending: purchasing } = usePurchaseTelephonyEndpoint();
  const hasPrefillAppliedRef = useRef(false);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.paper,
      borderRadius: '8px',
      '& fieldset': { borderColor: theme.palette.divider },
      '&:hover fieldset': { borderColor: theme.palette.grey[400] },
      '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main, borderWidth: 1 }
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      backgroundColor: theme.palette.grey[50],
      '& fieldset': { borderColor: theme.palette.divider }
    },
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary
    }
  };

  const providers = useMemo(
    () => purchaseProviders(businessProfile?.telephony_providers),
    [businessProfile?.telephony_providers]
  );
  const canSelfBuy = hasAnySelfBuy(providers);
  const twilioProvider = providers.find((p) => p.code === 'twilio');
  const bandwidthProvider = providers.find((p) => p.code === 'bandwidth');

  const countryCode = normalizeCountryCode(businessProfile?.address?.country || 'CA') || 'CA';
  const countryLabel = COUNTRY_LABELS[countryCode] || countryCode;

  const [providerCode, setProviderCode] = useState<'twilio' | 'bandwidth'>('twilio');
  const [searchValue, setSearchValue] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [searchParams, setSearchParams] = useState<Record<string, unknown> | null>(null);
  const [selectedNumber, setSelectedNumber] = useState('');

  const selectedProvider =
    providers.find((p) => p.code === providerCode) ?? twilioProvider ?? bandwidthProvider ?? providers[0];

  const { data: states = [] } = useGetStates(countryCode, {
    enabled: Boolean(countryCode) && open && isBusinessProfileFetched
  } as Parameters<typeof useGetStates>[1]);
  const stateOptions = useMemo(() => states.map((state) => String(state.value)), [states]);

  const defaultSearchValue = useMemo(() => {
    const rawState = businessProfile?.address?.state;
    const normalized = normalizeBusinessState(rawState);
    if (isValidPhoneNumberSearch(normalized)) return normalized;

    const matchedState = states.find(
      (state) => String(state.value).toUpperCase() === rawState?.trim().toUpperCase() || state.label === rawState
    );

    return matchedState ? String(matchedState.value) : '';
  }, [businessProfile?.address?.state, states]);

  const countryConfig = findWhitelistedCountry(selectedProvider?.whitelisted_countries, countryCode);
  const purchaseAccess = getPhoneNumberPurchaseAccess(countryConfig);
  const businessPlan = businessProfile?.business_plan;
  const monthlyProviderPrice = countryConfig?.price ?? 0;
  const monthlySurcharge = getPhoneNumberSurchargeAmount(businessPlan?.price_details);
  const phoneChargesEnabled = isPhoneNumberChargeEnabled(businessPlan?.price_details);
  const monthlyPhoneTotal = monthlyProviderPrice + (phoneChargesEnabled ? monthlySurcharge : 0);

  useEffect(() => {
    if (!open) {
      hasPrefillAppliedRef.current = false;
      return;
    }

    const initialCode = (twilioProvider?.code || bandwidthProvider?.code || 'twilio') as 'twilio' | 'bandwidth';
    setProviderCode(initialCode);
    setFriendlyName('');
    setSelectedNumber('');
  }, [open, twilioProvider, bandwidthProvider]);

  useEffect(() => {
    if (!open || hasPrefillAppliedRef.current || !defaultSearchValue) return;
    hasPrefillAppliedRef.current = true;
    setSearchValue(defaultSearchValue);
    setSearchInput(defaultSearchValue);
  }, [open, defaultSearchValue]);

  useEffect(() => {
    if (!open || !selectedProvider || purchaseAccess !== 'self_buy') {
      setSearchParams(null);
      return;
    }
    if (!isValidPhoneNumberSearch(searchValue)) {
      setSearchParams(null);
      setSelectedNumber('');
      return;
    }

    const filters = parsePhoneNumberSearch(searchValue);
    setSelectedNumber('');
    setSearchParams({
      provider: selectedProvider.code,
      telephony_provider_id: selectedProvider.id,
      country_code: countryCode,
      ...(filters.stateCode ? { region: filters.stateCode } : {}),
      ...(filters.areaCode ? { area_code: filters.areaCode } : {})
    });
  }, [open, selectedProvider, countryCode, searchValue, purchaseAccess, providerCode]);

  const {
    data: availableData,
    isFetching: searching,
    isError: searchFailed
  } = useGetAvailableEndpointNumbers(searchParams, { enabled: Boolean(searchParams) });

  const availableNumbers = availableData?.data ?? [];
  const regionLabel = parsePhoneNumberSearch(searchValue).stateCode || searchValue.toUpperCase();

  const onPurchase = async () => {
    if (!selectedProvider || !selectedNumber || purchaseAccess !== 'self_buy') return;
    await purchaseNumber({
      telephony_endpoint: {
        phone_number: selectedNumber,
        provider: selectedProvider.code as 'twilio' | 'bandwidth',
        telephony_provider_id: selectedProvider.id,
        country_code: countryCode,
        region: parsePhoneNumberSearch(searchValue).stateCode,
        friendly_name: friendlyName.trim() || undefined
      }
    });
    onClose();
  };

  const switchProvider = (code: 'twilio' | 'bandwidth') => {
    if (!providers.find((p) => p.code === code)) return;
    setProviderCode(code);
    setSelectedNumber('');
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 720,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${theme.palette.divider}`}
      >
        <Typography fontSize={18} fontWeight={500} color='text.primary'>
          Buy Phone Number
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close' sx={{ color: 'text.primary' }}>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      {!canSelfBuy || providers.length === 0 ? (
        <Box flex={1} display='flex' alignItems='center' justifyContent='center' px={4} py={6}>
          <Box display='flex' flexDirection='column' alignItems='center' textAlign='center' gap={2.5} maxWidth={420}>
            <NoCarrierIcon />
            <Typography color='text.secondary' fontSize={15} lineHeight={1.6}>
              No carrier has been assigned to you yet. Please contact
              {supportEmail ? (
                <>
                  {' '}
                  <Link href={`mailto:${supportEmail}`} underline='hover' fontWeight={600} color='primary.main'>
                    {supportEmail}
                  </Link>{' '}
                </>
              ) : (
                ' support '
              )}
              to request a carrier assignment.
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Box flex={1} overflow='auto' px={3} py={2.5}>
            <Box
              display='flex'
              mb={3}
              border={`1px solid ${theme.palette.border.main}`}
              borderRadius='12px'
              overflow='hidden'
            >
              <ProviderTab
                active={providerCode === 'twilio'}
                label='Twilio'
                disabled={!twilioProvider}
                onClick={() => switchProvider('twilio')}
              />
              <ProviderTab
                active={providerCode === 'bandwidth'}
                label='Bandwidth'
                disabled={!bandwidthProvider}
                onClick={() => switchProvider('bandwidth')}
              />
            </Box>

            {purchaseAccess !== 'self_buy' ? (
              <>
                <TextField
                  fullWidth
                  label='Country'
                  value={countryLabel}
                  disabled
                  sx={{ mb: 2.5, ...fieldSx }}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography color='text.secondary' fontSize={14} lineHeight={1.6}>
                  Self-serve purchase is not available for this country. Contact
                  {supportEmail ? (
                    <>
                      {' '}
                      <Link href={`mailto:${supportEmail}`} underline='hover' fontWeight={600} color='primary.main'>
                        {supportEmail}
                      </Link>{' '}
                    </>
                  ) : (
                    ' support '
                  )}
                  to request a number.
                </Typography>
              </>
            ) : (
              <>
                <Box
                  display='grid'
                  gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                  gap={2}
                  mb={0.75}
                  alignItems='flex-start'
                >
                  <TextField
                    fullWidth
                    label='Country'
                    value={countryLabel}
                    disabled
                    sx={fieldSx}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Autocomplete
                    freeSolo
                    fullWidth
                    disableClearable
                    options={stateOptions}
                    value={searchValue || undefined}
                    inputValue={searchInput}
                    onChange={(_, newValue) => {
                      const next = formatSearchInput(typeof newValue === 'string' ? newValue : newValue || '');
                      setSearchInput(next);
                      setSearchValue(next);
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      const next = formatSearchInput(newInputValue);
                      setSearchInput(next);
                      if (reason === 'input' || reason === 'clear') setSearchValue(next);
                    }}
                    filterOptions={(options, { inputValue: currentInput }) => {
                      if (/^\d/.test(currentInput.trim())) return [];
                      if (!currentInput.trim()) return options;
                      const input = currentInput.trim().toUpperCase();
                      return options.filter((option) => option.startsWith(input));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='State Code or Area Code'
                        autoComplete='off'
                        InputLabelProps={{ shrink: true }}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Box>
                <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2} mb={2.5}>
                  <Box display={{ xs: 'none', sm: 'block' }} />
                  <Typography fontSize={12} color='text.secondary'>
                    Select a state code or enter an area code.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label='Friendly Name'
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
                <Typography fontSize={12} color='text.secondary' mt={0.75} mb={3}>
                  Shown instead of the number in your lists. You can change it anytime.
                </Typography>

                {isValidPhoneNumberSearch(searchValue) && (
                  <>
                    <Box display='flex' alignItems='center' justifyContent='space-between' gap={2} mb={2}>
                      <Typography fontWeight={700} fontSize={15} color='text.primary'>
                        Select a Number
                      </Typography>
                      <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap' justifyContent='flex-end'>
                        <Typography fontWeight={700} fontSize={14} color='primary.main'>
                          {formatDollars(monthlyPhoneTotal, 9)}/month
                        </Typography>
                        {!searching && (
                          <Typography fontSize={13} color='text.secondary'>
                            {availableNumbers.length} available in {regionLabel}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {searching && (
                      <Box display='flex' justifyContent='center' py={4}>
                        <CircularProgress size={28} color='primary' />
                      </Box>
                    )}

                    {searchFailed && (
                      <Typography color='error.main' fontSize={13} mb={2}>
                        Could not load available numbers. Try a different filter.
                      </Typography>
                    )}

                    {!searching && !searchFailed && availableNumbers.length === 0 && (
                      <Typography color='text.secondary' fontSize={13} mb={2}>
                        No numbers found for that search.
                      </Typography>
                    )}

                    <Box
                      display='grid'
                      gridTemplateColumns={{ xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' }}
                      gap={1.5}
                    >
                      {availableNumbers.map((phone) => {
                        const selected = selectedNumber === phone;
                        return (
                          <Box
                            key={phone}
                            onClick={() => setSelectedNumber(phone)}
                            sx={{
                              cursor: 'pointer',
                              textAlign: 'center',
                              py: 1.1,
                              px: 1,
                              borderRadius: '24px',
                              border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                              color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                              bgcolor: selected ? theme.palette.primary.light : theme.palette.background.paper,
                              fontSize: 13,
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              '&:hover': {
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main
                              }
                            }}
                          >
                            {formatPhoneNumberDisplay(phone)}
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}
              </>
            )}
          </Box>

          <Box
            display='flex'
            justifyContent='flex-end'
            gap={1.5}
            px={3}
            py={2.5}
            borderTop={`1px solid ${theme.palette.divider}`}
            bgcolor='background.paper'
          >
            <Button
              variant='outlined'
              onClick={onClose}
              sx={{
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: 'text.primary',
                fontWeight: 500,
                px: 2.5,
                bgcolor: 'background.paper',
                '&:hover': {
                  borderColor: theme.palette.grey[400],
                  bgcolor: theme.palette.grey[50]
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={onPurchase}
              isPending={purchasing}
              disabled={!selectedNumber || purchaseAccess !== 'self_buy'}
              sx={{ textTransform: 'none', fontWeight: 500, boxShadow: 'none', px: 2.5 }}
            >
              Buy Number
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
}
